/**
 * Based on THREE.js TextureLoader.js. Modifications copyright Higharc Inc.
 */
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { isOnNodeJS } from "common/utils/context";
import { proxifyURL } from "common/utils/fetchHelpers";
import { productLog } from "common/utils/logging";
import * as THREE from "three";
import { HAVE_IMAGEBITMAP } from "../../constants";

type CollectImageLoadPromiseFunc = (promise: Promise<unknown>) => void;
export class ExtendedTextureLoader extends THREE.Loader {
  private static _instance: ExtendedTextureLoader | undefined;

  static get instance(): ExtendedTextureLoader {
    return this._instance ?? (this._instance = new ExtendedTextureLoader());
  }

  private constructor(manager?: THREE.LoadingManager) {
    super(manager);
  }

  private collectImageLoadPromiseCallback: CollectImageLoadPromiseFunc | null = null;

  setCollectImageLoadPromiseCallback(callback: CollectImageLoadPromiseFunc): void {
    this.collectImageLoadPromiseCallback = callback;
  }

  private async createLoader(): Promise<THREE.Loader> {
    if (isOnNodeJS()) {
      const NodeImageLoader = (await import("./nodeImageLoader")).default;
      assertIsNotUndefined(NodeImageLoader);
      return new NodeImageLoader(this.manager);
    } else if (HAVE_IMAGEBITMAP) {
      const loader = new THREE.ImageBitmapLoader(this.manager);
      loader.setOptions({ imageOrientation: "flipY" });
      return loader;
    } else {
      return new THREE.ImageLoader(this.manager);
    }
  }

  async loadWithoutRetry(
    url: string,
    onLoad?: (texture: THREE.Texture) => void,
    onError?: (event: ErrorEvent) => void,
    overrideFormat?: THREE.PixelFormat,
    texture: THREE.Texture = new THREE.Texture(),
  ): Promise<THREE.Texture> {
    // Proxify S3 URLs to avoid CORS issues (browser only)
    if (!isOnNodeJS()) {
      url = proxifyURL(url);
    }

    const loader = await this.createLoader();
    loader.setCrossOrigin(this.crossOrigin);
    loader.setPath(this.path);

    const promise = loader
      .loadAsync(url)
      .then((image) => {
        texture.image = image;
        if (overrideFormat !== undefined) {
          texture.format = overrideFormat;
        } else {
          texture.format = THREE.RGBAFormat;
        }
        texture.needsUpdate = true;
        if (onLoad !== undefined) {
          onLoad(texture);
        }
      })
      .catch((e: unknown) => onError?.(e as ErrorEvent));

    if (this.collectImageLoadPromiseCallback !== null) {
      this.collectImageLoadPromiseCallback(promise);
    }

    return texture;
  }

  async loadTexture({
    url,
    onLoad,
    onError,
    overrideFormat,
    texture = new THREE.Texture(),
  }: {
    url: string;
    onLoad?: (texture: THREE.Texture) => void;
    onError?: (event: ErrorEvent) => void;
    overrideFormat?: THREE.PixelFormat;
    texture?: THREE.Texture;
  }): Promise<THREE.Texture> {
    const retryOnError = () => {
      productLog("Loading texture failed on first try, retrying: " + url);
      setTimeout(async () => {
        await this.loadWithoutRetry(url, onLoad, onError, overrideFormat, texture);
      }, 1000);
    };

    await this.loadWithoutRetry(url, onLoad, retryOnError, overrideFormat, texture);
    return texture;
  }
}
